import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Pencil, Trash2 } from "lucide-react";
const DownloadsAdmin = () => {
  const [downloads, setDownloads] = useState([]);
  const [services, setServices] = useState([]);
  const [subservices, setSubservices] = useState([]);
  const [formData, setFormData] = useState({
    service: "",
    subservice: "",
    pdfFiles: [],
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  // Fetch all downloads
  const fetchDownloads = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/download/all`);
      setDownloads(data);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch all services
  const fetchServices = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/services/get-all-services-data`);
      setServices(data);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch subservices for selected service
  const fetchSubservices = async (serviceId) => {
    if (!serviceId) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/subservices/subservices-by-service/${serviceId}`
      );
      setSubservices(res.data.data || []);
    } catch (error) {
      console.error(error);
      setSubservices([]);
    }
  };

  useEffect(() => {
    fetchDownloads();
    fetchServices();
  }, []);

  useEffect(() => {
    fetchSubservices(formData.service);
  }, [formData.service]);

  // Handle form input
  const handleChange = (e) => {
    if (e.target.name === "pdfFiles") {
      setFormData({ ...formData, pdfFiles: e.target.files });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  // Upload PDFs to Cloudinary
  const uploadToCloudinary = async (file) => {
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);
    data.append("resource_type", "auto"); // supports PDFs, images, etc.
    const res = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/auto/upload`,
      data
    );
    return res.data.secure_url;
  };

  // Submit form (create or edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Step 1: Upload all selected PDFs to Cloudinary
      let uploadedUrls = [];
      if (formData.pdfFiles.length > 0) {
        uploadedUrls = await Promise.all(
          Array.from(formData.pdfFiles).map(uploadToCloudinary)
        );
      }

      // Step 2: Prepare payload
      const payload = {
        service: formData.service,
        subservice: formData.subservice,
        pdfFiles: uploadedUrls,
      };

      // Step 3: Send to backend (create or update)
      if (editingId) {
        await axios.put(`${process.env.REACT_APP_API_URL}/download/${editingId}`, payload);
        toast.success("Download updated successfully");
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/download/upload`, payload);
        toast.success("Download created successfully");
      }

      // Step 4: Reset form & refresh data
      setFormData({ service: "", subservice: "", pdfFiles: [] });
      setEditingId(null);
      fetchDownloads();
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong during upload");
    } finally {
      setLoading(false);
    }
  };

  // Edit download
  const handleEdit = (download) => {
    setEditingId(download._id);
    setFormData({
      service: download.service._id,
      subservice: download.subservice._id,
      pdfFiles: [],
    });
  };

  // Delete download
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this download?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/download/${id}`);
      toast.success("Download deleted");
      fetchDownloads();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete");
    }
  };
  const totalPages = Math.ceil(downloads.length / itemsPerPage);
  const paginatedDownloads = downloads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Downloads Management</h1>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-md shadow-md">
        <div className="mb-3">
          <label className="block mb-1">Service</label>
          <select
            name="service"
            value={formData.service}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          >
            <option value="">Select Service</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.serviceName}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="block mb-1">Subservice</label>
          <select
            name="subservice"
            value={formData.subservice}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          >
            <option value="">Select Subservice</option>
            {subservices.map((sub) => (
              <option key={sub._id} value={sub._id}>
                {sub.subserviceName}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="block mb-1">PDF Files</label>
          <input
            type="file"
            name="pdfFiles"
            onChange={handleChange}
            multiple
            className="w-full"
            accept="application/pdf"
          />
        </div>

        <button
          type="submit"
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          disabled={loading}
        >
          {loading ? "Uploading..." : editingId ? "Update" : "Create"}
        </button>
        {editingId && (
          <button
            type="button"
            onClick={() => {
              setEditingId(null);
              setFormData({ service: "", subservice: "", pdfFiles: [] });
            }}
            className="ml-2 bg-gray-300 px-4 py-2 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>
        )}
      </form>

      {/* Downloads Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-200 rounded-lg shadow-sm">
          <thead>
            <tr className="bg-gray-100 text-left text-sm font-semibold text-gray-600">
              <th className="border px-4 py-2">Service</th>
              <th className="border px-4 py-2">Subservice</th>
              <th className="border px-4 py-2">PDF Files</th>
              <th className="border px-4 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedDownloads.map((d) => (
              <tr key={d._id} className="hover:bg-gray-50 transition">
                <td className="border px-4 py-2 text-gray-700">
                  {d.service?.serviceName || "—"}
                </td>
                <td className="border px-4 py-2 text-gray-700">
                  {d.subservice?.subserviceName || "—"}
                </td>
                <td className="border px-4 py-2">
                  {d.pdfFiles?.length > 0 ? (
                    d.pdfFiles.map((file, idx) => (
                      <a
                        key={idx}
                        href={file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline block text-sm"
                      >
                        PDF {idx + 1}
                      </a>
                    ))
                  ) : (
                    <span className="text-gray-400 italic text-sm">No files</span>
                  )}
                </td>
                <td className="border px-4 py-2 text-center flex justify-center gap-3">
                  <button
                    onClick={() => handleEdit(d)}
                    className="p-2 bg-yellow-100 hover:bg-yellow-200 rounded-full transition"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4 text-yellow-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(d._id)}
                    className="p-2 bg-red-100 hover:bg-red-200 rounded-full transition"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls */}
      <div className="flex justify-center items-center gap-2 mt-4">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
          className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-100"
        >
          Prev
        </button>

        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            onClick={() => setCurrentPage(i + 1)}
            className={`px-3 py-1 rounded-md text-sm ${currentPage === i + 1
                ? "bg-blue-500 text-white"
                : "border hover:bg-gray-100"
              }`}
          >
            {i + 1}
          </button>
        ))}

        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => p + 1)}
          className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-100"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default DownloadsAdmin;



// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { toast } from "react-hot-toast";

// const DownloadsAdmin = () => {
//   const [downloads, setDownloads] = useState([]);
//   const [services, setServices] = useState([]);
//   const [subservices, setSubservices] = useState([]);
//   const [formData, setFormData] = useState({
//     service: "",
//     subservice: "",
//     pdfFiles: [],
//   });
//   const [editingId, setEditingId] = useState(null);
//   const [loading, setLoading] = useState(false);

//   // Fetch all downloads
//   const fetchDownloads = async () => {
//     try {
//       const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/download/all`);
//       setDownloads(data);
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   // Fetch all services
//   const fetchServices = async () => {
//     try {
//       const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/services/get-all-services-data`);
//      setServices(data)
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   // Fetch subservices by service
//   const fetchSubservices = async (serviceId) => {
//     if (!serviceId) return;
//     try {
//       const { data } = await axios.get(
//         `${process.env.REACT_APP_API_URL}/subservices/subservices-by-service/${serviceId}`
//       );
//       setSubservices(data.data || []);
//     } catch (error) {
//       console.error(error);
//       setSubservices([]);
//     }
//   };

//   useEffect(() => {
//     fetchDownloads();
//     fetchServices();
//   }, []);

//   useEffect(() => {
//     fetchSubservices(formData.service);
//   }, [formData.service]);

//   const handleChange = (e) => {
//     if (e.target.name === "pdfFiles") {
//       setFormData({ ...formData, pdfFiles: e.target.files });
//     } else {
//       setFormData({ ...formData, [e.target.name]: e.target.value });
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     try {
//       const data = new FormData();
//       data.append("service", formData.service);
//       data.append("subservice", formData.subservice);
//       for (let i = 0; i < formData.pdfFiles.length; i++) {
//         data.append("pdfFiles", formData.pdfFiles[i]);
//       }

//       if (editingId) {
//         await axios.put(`${process.env.REACT_APP_API_URL}/download/${editingId}`, data);
//         toast.success("Download updated successfully");
//       } else {
//         await axios.post(`${process.env.REACT_APP_API_URL}/download/upload`, data);
//         toast.success("Download created successfully");
//       }

//       setFormData({ service: "", subservice: "", pdfFiles: [] });
//       setEditingId(null);
//       fetchDownloads();
//     } catch (error) {
//       console.error(error);
//       toast.error("Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEdit = (download) => {
//     setEditingId(download._id);
//     setFormData({
//       service: download.service._id,
//       subservice: download.subservice._id,
//       pdfFiles: [],
//     });
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm("Are you sure you want to delete this download?")) return;
//     try {
//       await axios.delete(`${process.env.REACT_APP_API_URL}/download/${id}`);
//       toast.success("Download deleted");
//       fetchDownloads();
//     } catch (error) {
//       console.error(error);
//       toast.error("Failed to delete");
//     }
//   };

//   return (
//     <div className="p-6">
//       <h1 className="text-2xl font-bold mb-4">Downloads Management</h1>

//       <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-md shadow-md">
//         <div className="mb-3">
//           <label>Service</label>
//           <select
//             name="service"
//             value={formData.service}
//             onChange={handleChange}
//             className="w-full border px-3 py-2 rounded-md"
//             required
//           >
//             <option value="">Select Service</option>
//             {services.map((s) => (
//               <option key={s._id} value={s._id}>
//                 {s.serviceName}
//               </option>
//             ))}
//           </select>
//         </div>

//         <div className="mb-3">
//           <label>Subservice</label>
//           <select
//             name="subservice"
//             value={formData.subservice}
//             onChange={handleChange}
//             className="w-full border px-3 py-2 rounded-md"
//             required
//           >
//             <option value="">Select Subservice</option>
//             {subservices.map((sub) => (
//               <option key={sub._id} value={sub._id}>
//                 {sub.subserviceName}
//               </option>
//             ))}
//           </select>
//         </div>

//         <div className="mb-3">
//           <label>PDF Files</label>
//           <input
//             type="file"
//             name="pdfFiles"
//             onChange={handleChange}
//             multiple
//             accept="application/pdf"
//             className="w-full"
//           />
//         </div>

//         <button
//           type="submit"
//           disabled={loading}
//           className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
//         >
//           {editingId ? "Update" : "Create"}
//         </button>
//         {editingId && (
//           <button
//             type="button"
//             onClick={() => setEditingId(null) && setFormData({ service: "", subservice: "", pdfFiles: [] })}
//             className="ml-2 bg-gray-300 px-4 py-2 rounded-md hover:bg-gray-400"
//           >
//             Cancel
//           </button>
//         )}
//       </form>

//       <table className="w-full border-collapse border">
//         <thead>
//           <tr className="bg-gray-100">
//             <th className="border px-3 py-2">Service</th>
//             <th className="border px-3 py-2">Subservice</th>
//             <th className="border px-3 py-2">PDF Files</th>
//             <th className="border px-3 py-2">Actions</th>
//           </tr>
//         </thead>
//         <tbody>
//           {downloads.map((d) => (
//             <tr key={d._id} className="hover:bg-gray-50">
//               <td className="border px-3 py-2">{d.service.serviceName}</td>
//               <td className="border px-3 py-2">{d.subservice.subserviceName}</td>
//               <td className="border px-3 py-2">
//                 {d.pdfFiles?.map((file, idx) => (
//                   <a
//                     key={idx}
//                     href={`${process.env.REACT_APP_API_URL}/${file}`}
//                     target="_blank"
//                     rel="noopener noreferrer"
//                     className="text-blue-500 hover:underline block"
//                   >
//                     PDF {idx + 1}
//                   </a>
//                 ))}
//               </td>
//               <td className="border px-3 py-2">
//                 <button onClick={() => handleEdit(d)} className="bg-yellow-400 px-2 py-1 rounded mr-2 hover:bg-yellow-500">
//                   Edit
//                 </button>
//                 <button
//                   onClick={() => handleDelete(d._id)}
//                   className="bg-red-500 px-2 py-1 rounded hover:bg-red-600 text-white"
//                 >
//                   Delete
//                 </button>
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//     </div>
//   );
// };

// export default DownloadsAdmin;
